import {
  useGetInvoicesSuspense,
  useGetPartiesSuspense,
} from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewHeader,
  PaymentViewLayout,
} from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';

import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';

import { PartyView } from './PartyView';
import { PayoutModal } from './PayoutModal';

type Props = {
  workspaceId: string;
  externalId: string;
};

const PartyDetailPageContent = ({
  workspaceId,
  externalId,
}: Props): JSX.Element => {
  useDocumentTitle('Party Details');
  const [showPayoutModal, setShowPayoutModal] = useState(false);

  const { data: partiesData } = useGetPartiesSuspense();
  const { data: invoicesData } = useGetInvoicesSuspense();
  const [, setLocation] = useLocation();

  // Find the specific party by externalId
  const party = useMemo(
    () => partiesData.data.find((p) => p.externalId === externalId),
    [partiesData, externalId]
  );

  // Find all invoices that include this party
  const partyInvoices = useMemo(
    () =>
      invoicesData.data.filter((invoice) => {
        // Check if party is the buyer
        if (invoice.buyerParty === externalId) return true;

        // Check if party is a seller in any line item
        if (!invoice.lineItems || invoice.lineItems.length === 0) return false;
        return invoice.lineItems.some(
          (item) =>
            !('platform' in item.payout_party) &&
            item.payout_party.party_id === externalId
        );
      }),
    [invoicesData, externalId]
  );

  const handleBackToParties = () => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'parties'], { absolute: true })
    );
  };

  if (!party) {
    return (
      <PaymentViewLayout>
        <div className="text-red-500 text-medium">
          Party not found: {externalId}
        </div>
      </PaymentViewLayout>
    );
  }

  const handlePayoutClick = () => {
    setShowPayoutModal(true);
  };

  return (
    <>
      <PaymentViewLayout
        header={
          <PaymentViewHeader>
            <div className="flex flex-row justify-between h-full w-full">
              <button
                type="button"
                onClick={handleBackToParties}
                className="text-main-500 hover:text-main flex items-start gap-f1 text-medium"
              >
                ←Parties
              </button>
              <div className="flex gap-f2">
                <PrimaryButtonV3 onClick={handlePayoutClick}>
                  Payout
                </PrimaryButtonV3>
              </div>
            </div>
          </PaymentViewHeader>
        }
      >
        <PartyView
          party={party}
          partyInvoices={partyInvoices}
          externalId={externalId}
          workspaceId={workspaceId}
          enableInvoiceNavigation
        />
      </PaymentViewLayout>
      {showPayoutModal && (
        <PayoutModal
          onClose={() => setShowPayoutModal(false)}
          invoices={partyInvoices
            .filter((invoice) => invoice.status === 'active')
            .map((invoice) => {
              // Calculate total amount from line items for this party
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const isBuyer = (invoice as any).buyerParty === externalId;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const lineItems = (invoice as any).lineItems || [];
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const partyLineItems = lineItems.filter((item: any) =>
                isBuyer
                  ? true
                  : !('platform' in item.payout_party) &&
                    item.payout_party.party_id === externalId
              );
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const totalAmount = partyLineItems.reduce(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (sum: number, item: any) => sum + Number(item.amount),
                0
              );

              return {
                id: invoice.id,
                externalId: invoice.ik,
                amount: totalAmount.toString(),
                currency: 'USD',
                status: invoice.status,
                createdAt: invoice.created,
              };
            })}
          partyName={externalId}
        />
      )}
    </>
  );
};

export const PartyDetailPage = ({
  workspaceId,
  externalId,
}: Props): JSX.Element => (
  <Suspense
    fallback={
      <PaymentViewLayout>
        <div className="text-main-500 text-medium">Loading party...</div>
      </PaymentViewLayout>
    }
  >
    <PartyDetailPageContent workspaceId={workspaceId} externalId={externalId} />
  </Suspense>
);
